import type { CityConfig } from "../hawaii-city/types";

export const kailuaConfig: CityConfig = {
  city: "Kailua",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "kailua",
  route: "/cities/kailua",
  title: "Emergency & Expedited Passport Services in Kailua, HI — Same-Day Processing | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Kailua, HI — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Windward Oahu and Lanikai travelers.",
  h1: "Emergency & Expedited Passport Services in Kailua, HI",
  heroSubtitle: "Passport expired? Flying soon? We help Kailua and Windward Oahu travelers get their passports fast. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20Kailua%20Beach%20Hawaii%20with%20turquoise%20waters%20and%20white%20sand%2C%20Mokulua%20Islands%20in%20background%2C%20lush%20green%20Windward%20Oahu%20mountains%2C%20bright%20sunny%20tropical%20day%20with%20crystal%20clear%20sky%2C%20professional%20travel%20photography%2C%20vibrant%20teal%20and%20green%20tropical%20colors%2C%20no%20readable%20text%20or%20signs%2C%20idyllic%20beach%20paradise%20atmosphere&width=1800&height=700&seq=kailua-hero-01&orientation=landscape",
  heroImageAlt: "Kailua Beach with turquoise waters and Mokulua Islands on Windward Oahu",
  geoPosition: "21.3994;-157.7393",
  geoPlacename: "Kailua, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "About 15 miles across Oahu",
  },
  nearbyCitiesZips: [
    { name: "Kailua", zips: "96734" },
    { name: "Lanikai", zips: "96734" },
    { name: "Waimanalo", zips: "96795" },
    { name: "Kaneohe", zips: "96744" },
    { name: "Kahaluu", zips: "96744" },
    { name: "Ahuimanu", zips: "96744" },
    { name: "Honolulu", zips: "96815" },
    { name: "Kaaawa", zips: "96730" },
    { name: "Hauula", zips: "96717" },
    { name: "Punaluu", zips: "96717" },
    { name: "Laie", zips: "96762" },
    { name: "Kahuku", zips: "96731" },
    { name: "Pupukea", zips: "96712" },
    { name: "Haleiwa", zips: "96712" },
    { name: "Waialua", zips: "96791" },
    { name: "Pearl City", zips: "96782" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping to send your documents directly to us — we handle the rest." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed." },
  ],
  testimonials: [
    {
      name: "Emily T.",
      location: "Kailua, HI",
      rating: 5,
      text: "Last-minute trip to Bali for a yoga retreat and my passport was about to expire. Fast Passport Center processed my renewal in 4 days. I was able to ship from Kailua and track everything online. So easy.",
      service: "Passport Renewal",
    },
    {
      name: "Chris M.",
      location: "Lanikai, HI",
      rating: 5,
      text: "Our honeymoon to Tahiti was in 2 weeks and my fiancé's passport was lost during a move. Fast Passport Center got him a replacement in under a week. They even helped us find the closest acceptance clerk in Kailua.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Jennifer W.",
      location: "Kaneohe, HI",
      rating: 5,
      text: "Military spouse with a sudden PCS move to Germany. Needed an expedited passport for my daughter. Fast Passport Center made the entire process seamless from Windward Oahu. Highly recommend for military families.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "kailua-usps",
      name: "Kailua Post Office",
      type: "USPS Passport Acceptance",
      address: "130 Kailua Rd",
      city: "Kailua",
      state: "HI",
      zip: "96734",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Kailua",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary post office for Kailua and Windward Oahu. Appointment required. Confirm photo services.",
      icon: "ri-mail-line",
    },
    {
      id: "kailua-kaneohe",
      name: "Kaneohe Post Office",
      type: "USPS Passport Acceptance",
      address: "45-690 Kamehameha Hwy",
      city: "Kaneohe",
      state: "HI",
      zip: "96744",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Kaneohe — about 5 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Nearby Kaneohe option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "kailua-waimanalo",
      name: "Waimanalo Post Office",
      type: "USPS Passport Acceptance",
      address: "41-1295 Kalanianaole Hwy",
      city: "Waimanalo",
      state: "HI",
      zip: "96795",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Waimanalo — about 8 miles southeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Waimanalo area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "kailua-honolulu",
      name: "Honolulu Main Post Office",
      type: "USPS Passport Acceptance",
      address: "3350 Manoa Rd",
      city: "Honolulu",
      state: "HI",
      zip: "96822",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Honolulu — about 15 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Honolulu option for Kailua residents. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "kailua-haleiwa",
      name: "Haleiwa Post Office",
      type: "USPS Passport Acceptance",
      address: "66-437 Kamehameha Hwy",
      city: "Haleiwa",
      state: "HI",
      zip: "96712",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "North Shore — about 25 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Shore option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "kailua-laie",
      name: "Laie Post Office",
      type: "USPS Passport Acceptance",
      address: "55-510 Kamehameha Hwy",
      city: "Laie",
      state: "HI",
      zip: "96762",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Laie — about 15 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Shore option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Kailua, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours depending on your eligibility and the processing option you select. Standard government processing through a post office in Kailua can take 2–6 months.",
    },
    {
      q: "Where is the closest passport agency to Kailua?",
      a: "The Honolulu Passport Agency is located at 300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850 — about 15 miles from Kailua across the Pali Highway or H-3. Regional agencies require appointments and are primarily reserved for life-or-death emergencies. Fast Passport Center can often provide a faster, more convenient option.",
    },
    {
      q: "Do I need to visit an office to use Fast Passport Center?",
      a: "No — most Kailua travelers start online and ship their documents via overnight courier. There is no local Fast Passport Center office in Kailua, but that's the point: you don't need one. Start your order online, follow the checklist, and ship your documents overnight.",
    },
    {
      q: "What documents do I need for an expedited passport?",
      a: "Most applicants need: proof of U.S. citizenship (current or expired U.S. passport, birth certificate, or naturalization certificate), a government-issued photo ID, completed passport forms, and passport photos. We provide a personalized checklist after you start your order.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants typically require an in-person acceptance clerk visit before documents can be submitted for expedited processing. We guide you through exactly what to do and where to go in the Kailua area.",
    },
    {
      q: "How long does a passport take through the post office in Kailua?",
      a: "Standard government processing through a Kailua acceptance facility can take 2–6 months depending on government workload. If your travel date is sooner than that, using an approved passport courier like Fast Passport Center is the safer option.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We are not a government agency. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport in Kailua?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions and a checklist. Expedited processing options are available after the acceptance step.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide you through exactly what's needed and help ensure the correct process is followed to avoid delays.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Kailua", address: "600 Kailua Rd, Kailua, HI 96734", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Kailua", address: "1090 Keolu Dr, Kailua, HI 96734", hours: "Daily 7 AM – 10 PM" },
    { name: "Walgreens — Kaneohe", address: "46-047 Kamehameha Hwy, Kaneohe, HI 96744", hours: "Daily 7 AM – 11 PM" },
    { name: "The UPS Store — Kailua", address: "322 Keolu Dr, Kailua, HI 96734", hours: "Call for hours" },
    { name: "FedEx Office — Kailua", address: "355 Hahani St, Kailua, HI 96734", hours: "Call for hours" },
    { name: "Walmart — Kaneohe", address: "46-021 Alaloa St, Kaneohe, HI 96744", hours: "Call for hours" },
    { name: "CVS Pharmacy — Kaneohe", address: "45-480 Kaneohe Bay Dr, Kaneohe, HI 96744", hours: "Daily 7 AM – 10 PM" },
    { name: "Walgreens — Waimanalo", address: "41-1531 Kalanianaole Hwy, Waimanalo, HI 96795", hours: "Daily 7 AM – 11 PM" },
  ],
  dmvLocations: [
    {
      name: "Kailua Satellite City Hall",
      address: "408 Kaku St, Kailua, HI 96734",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kaneohe Satellite City Hall",
      address: "45-270 Waikalua Rd, Kaneohe, HI 96744",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Kaneohe area option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "Primary airport — about 15 miles from Kailua",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub for Hawaii. Kailua travelers access via Pali Highway or H-3.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: true,
    },
    {
      code: "OGG",
      name: "Kahului Airport (Maui)",
      distance: "Inter-island flights — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Major inter-island airport serving Maui with connections to Honolulu.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel. We prioritize your application and submit directly via reserved agency slots.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Kailua and Windward Oahu travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide you through every step of the process.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes and delays.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions and checklists.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports and data corrections.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a significantly damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Kailua and Windward Oahu travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Kailua residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Kailua residents with step-by-step guidance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Kailua residents with lost or stolen passports." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Kailua, with parental consent guidance." },
    { name: "Name Change", description: "Passport correction and name change service for Kailua residents after marriage or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Kailua travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Kailua travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Kailua clients ship from home — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to travel or wait at a passport agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–3", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — drive to downtown Honolulu", fpc: "Most travelers ship documents overnight", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Honolulu", route: "/cities/honolulu" },
    { name: "Kaneohe", route: "/cities/kaneohe" },
    { name: "Haleiwa", route: "/haleiwa" },
    { name: "Pearl City", route: "/cities/pearl-city" },
    { name: "Waimanalo", route: "/waimanalo" },
    { name: "Hilo", route: "/cities/hilo" },
    { name: "Laie", route: "/laie" },
    { name: "Kahuku", route: "/kahuku" },
  ],
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your home or office in most cases, ship with our prepaid label from any UPS/FedEx location, or schedule a drop-off. Most clients ship overnight — no visit required.",
  fpcOfficeDistance: "Serving Kailua and Windward Oahu remotely",
  serviceAreaIntro: "Whether you are in Kailua, Lanikai, Waimanalo, Kaneohe, or anywhere on Windward Oahu, Fast Passport Center makes expedited passport processing simple — no agency lines, no guessing.",
  nearbyCitiesIntro: "Anywhere on Windward Oahu — start online, ship overnight, and we handle the rest. No need to travel off-island.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Kailua as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Kailua and Windward Oahu travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Kailua, Lanikai, and Kaneohe who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Kailua and Windward Oahu travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Oahu",
  islandNickname: "The Gathering Place",
  landmarkCards: [
    {
      name: "Kailua Beach Park",
      description: "Consistently rated one of the best beaches in the U.S., Kailua Beach is a stunning stretch of white sand and turquoise water. Many travelers have their 'I need to renew my passport' moment right here.",
      imageUrl: "https://readdy.ai/api/search-image?query=Kailua%20Beach%20Oahu%20Hawaii%20with%20turquoise%20calm%20water%20and%20white%20sand%2C%20Mokulua%20Islands%20visible%20in%20distance%2C%20gentle%20waves%2C%20tropical%20blue%20sky%2C%20professional%20travel%20photography%2C%20stunning%20beach%20paradise%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kailua-beach&orientation=landscape",
    },
    {
      name: "Lanikai Beach",
      description: "A secluded gem tucked behind Kailua, Lanikai is frequently called the most beautiful beach in the world. Residents here often travel internationally for luxury experiences — passports must be current.",
      imageUrl: "https://readdy.ai/api/search-image?query=Lanikai%20Beach%20Kailua%20Hawaii%20with%20crystal%20turquoise%20water%2C%20small%20tropical%20islands%20in%20background%2C%20white%20sand%20shoreline%2C%20palm%20trees%2C%20warm%20golden%20sunlight%2C%20professional%20travel%20photography%2C%20paradise%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kailua-lanikai&orientation=landscape",
    },
    {
      name: "Pali Lookout",
      description: "The dramatic Nu'uanu Pali Lookout offers sweeping views over Windward Oahu. Historic battleground turned scenic viewpoint — a symbol of Oahu's layered history.",
      imageUrl: "https://readdy.ai/api/search-image?query=Nuuanu%20Pali%20Lookout%20Oahu%20Hawaii%20with%20dramatic%20cliff%20views%2C%20lush%20green%20Windward%20Oahu%20valleys%20below%2C%20distant%20ocean%20visible%2C%20dramatic%20clouds%2C%20strong%20winds%2C%20professional%20landscape%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kailua-pali&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-flight-takeoff-line", title: "HNL via H-3 or Pali", description: "Kailua residents reach HNL through the Pali Highway or H-3 tunnel. Budget 30–45 minutes to the airport and ensure passport validity before every international booking." },
    { icon: "ri-shield-star-line", title: "Military Families Welcome", description: "Marine Corps Base Hawaii (MCBH) is adjacent to Kailua. We frequently help military families on PCS moves and TDY assignments. Urgent processing is available." },
    { icon: "ri-truck-line", title: "Ship from Kailua Town", description: "FedEx and UPS locations in Kailua Town Center make overnight shipping simple. Get documents out before the daily cutoff and we receive them the next morning." },
    { icon: "ri-sun-line", title: "Active Lifestyle = Active Travel", description: "Kailua residents are known for outdoor adventures — kayaking, kiteboarding, hiking. International surf trips and adventure travel often require valid passports on short notice." },
    { icon: "ri-calendar-event-line", title: "School Year Travel Windows", description: "Kailua families often travel to Asia and Australia during school holidays. Summer and winter breaks are peak processing times — renew passports in advance." },
    { icon: "ri-user-location-line", title: "Remote Work Travelers", description: "Kailua has a growing remote worker community traveling internationally. Passport renewal needs come up when working from abroad — plan for expedited processing." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
};